import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { TagOption, TagRefModel, TagValueModel } from "../utils/Types";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { Skeleton } from "./ui/skeleton";
import TagCloud from "./TagCloud.tsx";
import { useAuth } from "../utils/AuthContext.tsx";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "./ui/button.tsx";
import { ExternalLink, Sparkle } from "lucide-react";
import {
  useContactDetail,
  useContactTags,
  useCreateContactTag,
  useDeleteContactTag,
  useCreateContactSummary,
} from "../utils/Api.ts";

interface ContactDetailProps {
  contactId: string;
}

const ContactDetail: React.FC<ContactDetailProps> = ({ contactId }) => {
  const isMobile = useIsMobile();
  const { token } = useAuth();

  const { data: contact } = useContactDetail(contactId, token ?? undefined);
  const { data: selectedTagValues, isFetching: isFetchingTagValues } = useContactTags(contactId, token ?? undefined);
  const { mutate: addContactTag } = useCreateContactTag(token ?? undefined);
  const { mutate: removeContactTag } = useDeleteContactTag(token ?? undefined);
  const { mutate: createContactSummary, isPending: isCreatingSummary } = useCreateContactSummary(token ?? undefined);

  //custom sort function for searching master format tags
  const mfFilter = (value: string, search: string, keywords?: string[]) => {
    if (keywords && keywords.length > 0) {
      const normalized = search.toLowerCase().replace(/\s/, "");
      for (const k of keywords) {
        if (k.startsWith(normalized)) return 1;
      }
    }
    const lower = value.toLowerCase();
    if (lower.includes(search.toLowerCase())) return 0.8;
    return 0;
  };

  const options: TagOption[] = [
    {
      name: "mf-2020",
      color: "bg-lime-200",
      label: "CSI Master Format",
      filter: mfFilter,
      lock: false,
      icon: "Blocks",
    },
    {
      name: "service",
      color: "bg-amber-300",
      label: "Service",
      lock: false,
      icon: "ListCheck",
    },
  ];

  const sortedValues = useMemo(() => {
    return selectedTagValues ? [...selectedTagValues].sort((a, b) => a.path.localeCompare(b.path)) : undefined;
  }, [selectedTagValues]);

  const handleOpenChange = (open: boolean) => {
    console.log("contact detail open:", open);
  };

  const handleRefreshSummary = async () => {
    if (contact) {
      createContactSummary(contact);
    }
  };

  return (
    <Sheet
      open={isMobile ? contact != undefined : true}
      modal={isMobile ? true : false}
      onOpenChange={handleOpenChange}
    >
      <SheetContent
        className="py-8 shadow-none h-[80vh] overflow-y-scroll overflow-x-clip sm:h-[100vh] lg:inline lg:px-8 lg:[&>button]:hidden xl:max-w-[640px] lg:max-w-[360px] xl:pl-12"
        side={isMobile ? "bottom" : "right"}
      >
        <SheetHeader>
          <div className="flex justify-end pb-4">
            <Button onClick={handleRefreshSummary} disabled={isCreatingSummary} variant="outline">
              <Sparkle />
            </Button>
          </div>
          <SheetTitle className="flex flex-col justify-start text-xl text-accent-foreground">
            {contact && contact.displayName}
            {isCreatingSummary && <Skeleton className="w-2/3 h-4 my-2" />}
            {!isCreatingSummary && (
              <>
                <div className="text-base">{contact && contact.summary?.headline}</div>
                <div className="text-base">{contact && contact.summary?.location}</div>
              </>
            )}
          </SheetTitle>
        </SheetHeader>
        <div className="pt-8 text-sm text-muted-foreground">
          {!contact && (
            <div className="flex flex-col gap-2">
              <Skeleton className="w-2/3 h-4" />
              <Skeleton className="w-3/4 h-4" />
              <Skeleton className="w-1/3 h-4" />
            </div>
          )}
          {contact && (
            <>
              <div className="pb-4">
                <TagCloud
                  prompt="Add Tags"
                  options={options}
                  selections={sortedValues}
                  onTagAdded={(value: TagValueModel) =>
                    addContactTag({ value: { id: "", path: value.path, source: "user" }, contactId: contactId })
                  }
                  onTagRemoved={(value: TagRefModel) => removeContactTag({ tagId: value.id, contactId: contactId })}
                  loading={isFetchingTagValues}
                />
              </div>
              {contact && (
                <div className="[all:unset]">
                  <div className="markdown-container rounded-md mb-16 mt-8 p-0 relative">
                    {isCreatingSummary && (
                      <div className="flex flex-col gap-2">
                        <Skeleton className="w-full h-4" />
                        <Skeleton className="w-full h-4" />
                        <Skeleton className="w-1/2 h-4" />
                      </div>
                    )}
                    {!isCreatingSummary && (
                      <ReactMarkdown children={contact.summary?.summary} remarkPlugins={[remarkGfm]} />
                    )}

                    {!isCreatingSummary && contact.summary?.options && (
                      <ul>
                        {contact.summary?.options.map((option: any, i: number) => (
                          <li key={i}>
                            {option.type == "email" && "✉️ "}
                            {option.type == "phone" && "📞 "}
                            {option.type == "mobile" && "📱 "}
                            {option.type == "location" && "📍 "}
                            {option.value} ({option.purpose})
                          </li>
                        ))}
                      </ul>
                    )}

                    {!isCreatingSummary && contact.summary?.business && (
                      <div className="py-4">
                        <div className="font-semibold text-base">{contact.summary?.business.name}</div>
                        {contact.summary?.business.website != "" && (
                          <div className="flex items-center">
                            <Link target="_new" to={contact.summary?.business.website}>
                              {contact.summary?.business.website}
                            </Link>
                            <ExternalLink size="12" />
                          </div>
                        )}
                        <ReactMarkdown children={contact.summary?.business.summary} remarkPlugins={[remarkGfm]} />
                      </div>
                    )}
                  </div>
                </div>
              )}
              {contact.n.length > 0 && (
                <div className="py-2">
                  <h4 className="text-base font-semibold">Names</h4>
                  {contact &&
                    contact.n.map((n: any, index: number) => (
                      <div key={index}>
                        <div>{n.honorificPrefix}</div>
                        <div>{n.given}</div>
                        <div>{n.family}</div>
                        <div>{n.additional}</div>
                        <div>{n.honorificSuffix}</div>
                      </div>
                    ))}
                </div>
              )}
              <div className="py-2">
                <h4 className="text-base font-semibold">Contact ID</h4>
                <div className="text-sm">{contactId}</div>
              </div>

              {(contact.title || contact.org) && (
                <div className="py-2">
                  <h4 className="text-base font-semibold">Title / Organization</h4>
                  <div>{contact && contact.title}</div>
                  <div>{contact && contact.org}</div>
                  <div>{contact && contact.role}</div>
                </div>
              )}
              {contact.emails.length > 0 && (
                <div className="py-2">
                  <h4 className="text-base font-semibold">Emails</h4>
                  {contact && contact.emails.map((e: any, index: number) => <div key={index}>{e}</div>)}
                </div>
              )}
              {contact.phones.length > 0 && (
                <div className="py-2">
                  <h4 className="text-base font-semibold">Phone Numbers</h4>
                  {contact && contact.phones.map((p: any, index: number) => <div key={index}>{p}</div>)}
                </div>
              )}
              {contact.notes.length > 0 && (
                <div className="py-2">
                  <h4 className="text-base font-semibold">Notes</h4>
                  {contact && contact.notes.map((n: any, index: number) => <div key={index}>{n}</div>)}
                </div>
              )}
              {contact?.addresses.length > 0 && (
                <div className="py-2">
                  <h4 className="text-base font-semibold">Addresses</h4>
                  {contact &&
                    contact?.addresses.map((a: any, index: number) => (
                      <div key={index}>
                        <div>{a.postOfficeBox}</div>
                        <div>
                          {a.streetAddress} {a.extendedAddress}
                        </div>
                        <div>
                          {a.locality}, {a.region} {a.postalCode}
                        </div>
                        <div>{a.countryName}</div>
                      </div>
                    ))}
                </div>
              )}
              {contact?.urls.length > 0 && (
                <div className="py-2">
                  <h4 className="text-base font-semibold">URLs</h4>
                  {contact && contact?.urls.map((u: any, index: number) => <div key={index}>{u}</div>)}
                </div>
              )}
              {contact?.photos.length > 0 && (
                <div className="py-2">
                  <h4 className="text-base font-semibold">Photos</h4>
                  {contact && contact.photos.map((p: any, index: number) => <div key={index}>{p}</div>)}
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ContactDetail;
